// Comercial > Contactos: pure logic -- testable with node (contactosLogica.test.mjs).
// The list's filters, search and order, its columns, and the contact's form
// (emails and phones with one principal, what to send, duplicates by name).
import { sinTildes } from './organizacionesLogica.js'

// --- Filters (in the address, like Organizaciones) ---

const FILTROS = ['q', 'organizacion', 'etiqueta', 'estado', 'sinorg']

// estado: 'no' (active, the default) | 'si' (archived) | 'todos' -- the backend's `archivados`.
// organizacion: its contacts and its subordinates'; sinorg: contacts with no
// active link (applied here, not by the server).
export function filtrosDeParams(params) {
  const estado = params.get('estado')
  const organizacion = Number(params.get('organizacion'))
  const etiqueta = Number(params.get('etiqueta'))
  return {
    q: params.get('q') || '',
    organizacion: organizacion > 0 ? organizacion : null,
    etiqueta: etiqueta > 0 ? etiqueta : null,
    estado: estado === 'si' || estado === 'todos' ? estado : 'no',
    sinorg: params.get('sinorg') === '1',
  }
}

// The address params with these filters, keeping the rest (the open card).
export function paramsConFiltros(params, f) {
  const next = new URLSearchParams(params)
  FILTROS.forEach(k => next.delete(k))
  if (f.q?.trim()) next.set('q', f.q.trim())
  if (f.organizacion) next.set('organizacion', String(f.organizacion))
  if (f.etiqueta) next.set('etiqueta', String(f.etiqueta))
  if (f.estado && f.estado !== 'no') next.set('estado', f.estado)
  if (f.sinorg) next.set('sinorg', '1')
  return next
}

export const hayFiltros = (f) => !!(f.q.trim() || f.organizacion || f.etiqueta || f.estado !== 'no' || f.sinorg)

// The filters behind the "Filtros" button that are on, for its counter.
export const contarFiltrosAdicionales = (f) => [f.etiqueta, f.estado !== 'no', f.sinorg].filter(Boolean).length

// The query for GET /contactos (what the server filters): an organization
// takes its subordinates' contacts too. The search goes apart (buscarEnServidor).
export const consultaListado = (f) => ({
  organizacion: f.organizacion || undefined, subordinadas: f.organizacion ? true : undefined,
  etiqueta: f.etiqueta || undefined, archivados: f.estado,
})

// --- Search ---
// The server searches name, surnames and every email and phone; the page adds
// the organizations (their whole route) and the cargos. A row shows when every
// word is in its own text, or when the server found it (ids from GET ?q=).

export function textoContacto(c) {
  const vinculos = c.vinculos_activos || []
  return sinTildes([c.nombre, c.apellidos, c.correo_principal, c.telefono_principal,
    ...vinculos.map(v => v.ruta || v.organizacion?.nombre_comercial), ...vinculos.map(v => v.cargo)]
    .filter(Boolean).join(' '))
}

export function coincide(c, q) {
  const palabras = sinTildes(q).split(/\s+/).filter(Boolean)
  if (!palabras.length) return true
  const texto = textoContacto(c)
  return palabras.every(p => texto.includes(p))
}

//   f: { q, sinorg }; delServidor: Set of ids GET /contactos?q= found (null = no search)
export function filtrarEnPagina(lista, f, delServidor = null) {
  return (lista || []).filter(c =>
    (!f.sinorg || !(c.vinculos_activos || []).length)
    && (!f.q?.trim() || coincide(c, f.q) || !!delServidor?.has(c.id)))
}

// --- The list ---

// Its organizations and their cargos, in the same order ("—" for a missing cargo
// while some other has one, so both lines stay aligned).
export const organizacionesDe = (c) => (c.vinculos_activos || []).map(v => v.organizacion?.nombre_comercial || '')
export function cargosDe(c) {
  const cargos = (c.vinculos_activos || []).map(v => v.cargo || '')
  return cargos.some(Boolean) ? cargos.map(x => x || '—') : []
}

const VALOR = {
  nombre: (c) => [c.nombre, c.apellidos].filter(Boolean).join(' '),
  organizaciones: (c) => organizacionesDe(c).join(', '),
  cargo: (c) => cargosDe(c).filter(x => x !== '—').join(', '),
  correo: (c) => c.correo_principal,
  telefono: (c) => c.telefono_principal,
  etiquetas: (c) => (c.etiquetas || []).map(x => x.nombre).join(', '),
}
export const COLUMNAS_OPCIONALES = Object.keys(VALOR).filter(c => c !== 'nombre')
export const COLUMNAS_PREDETERMINADAS = ['organizaciones', 'cargo']

// A saved choice, cleaned (known columns only, screen order); not a list = the default.
export function columnasValidas(valor) {
  if (!Array.isArray(valor)) return [...COLUMNAS_PREDETERMINADAS]
  return COLUMNAS_OPCIONALES.filter(c => valor.includes(c))
}
export const alternarColumna = (columnas, c) =>
  columnasValidas(columnas.includes(c) ? columnas.filter(x => x !== c) : [...columnas, c])
export const ordenVisible = (orden, columnas) =>
  (orden.campo === 'nombre' || columnas.includes(orden.campo) ? orden : { campo: 'nombre', dir: 'asc' })

const vacio = (v) => v === null || v === undefined || v === ''
const comparar = (a, b) => sinTildes(a).localeCompare(sinTildes(b), undefined, { numeric: true })

// By name and then surnames (the default), or by another column; either way;
// empty values always last; ties by name, then id.
export function ordenarContactos(lista, { campo = 'nombre', dir = 'asc' } = {}) {
  const valor = VALOR[campo] || VALOR.nombre
  const signo = dir === 'desc' ? -1 : 1
  return [...(lista || [])].sort((a, b) => {
    const va = valor(a)
    const vb = valor(b)
    if (vacio(va) !== vacio(vb)) return vacio(va) ? 1 : -1
    const c = vacio(va) ? 0 : comparar(va, vb) * signo
    return c || comparar(VALOR.nombre(a), VALOR.nombre(b)) || a.id - b.id
  })
}

// --- Emails and phones: a list with at most one principal ---
// The first one added is the principal; removing the principal hands it to the first left.

const nuevaFila = (principal) => ({ valor: '', descripcion: '', principal })

export const agregarFila = (lista) => [...lista, nuevaFila(!lista.some(x => x.principal))]

export function quitarFila(lista, i) {
  const quedan = lista.filter((_, j) => j !== i)
  if (lista[i]?.principal && quedan.length) quedan[0] = { ...quedan[0], principal: true }
  return quedan
}

export const marcarPrincipal = (lista, i) => lista.map((x, j) => ({ ...x, principal: j === i }))

export const cambiarFila = (lista, i, campo, valor) => lista.map((x, j) => (j === i ? { ...x, [campo]: valor } : x))

// What the server takes: blank values dropped, text trimmed (emails in lowercase),
// exactly one principal when there are any (the marked one if it survived, else the first).
export function filasParaEnviar(lista, esCorreo = false) {
  const filas = (lista || [])
    .map(x => ({ valor: String(x.valor ?? '').trim(), descripcion: String(x.descripcion ?? '').trim() || null, principal: !!x.principal }))
    .filter(x => x.valor)
    .map(x => (esCorreo ? { ...x, valor: x.valor.toLowerCase() } : x))
  const p = Math.max(0, filas.findIndex(x => x.principal))
  return filas.map((x, i) => ({ ...x, principal: i === p }))
}

const CORREO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
export const correoValido = (s) => CORREO.test(String(s ?? '').trim())

// --- The form ---

const CAMPOS = ['nombre', 'apellidos', 'linkedin', 'observaciones']

// Whether the form offers organizations to link: always for a new contact, and
// for an existing one that has none (otherwise links are edited in its card's tab).
export const pideOrganizaciones = (c) => !c || !(c.vinculos_activos || []).length

// The form's values for a contact (GET /contactos/{id}), or for a new one,
// maybe already linked to an organization (created from its card). Where
// organizations are asked, one row is there from the start (empty, or the
// organization it was created from); an empty row is simply left out on saving.
export function formularioDe(c, organizacionInicial = null) {
  const f = {}
  for (const k of CAMPOS) f[k] = c ? (c[k] ?? '') : ''
  const filas = (xs) => (xs || []).map(x => ({ valor: x.valor, descripcion: x.descripcion || '', principal: !!x.principal }))
  f.correos = c ? filas(c.correos) : [nuevaFila(true)]
  f.telefonos = c ? filas(c.telefonos) : [nuevaFila(true)]
  f.etiqueta_ids = c ? (c.etiquetas || []).map(x => x.id) : []
  f.vinculos = pideOrganizaciones(c) ? [{ organizacion_id: c ? null : (organizacionInicial ?? null), cargo: '' }] : []
  return f
}

const limpio = (v) => (typeof v === 'string' ? (v.trim() || null) : (v ?? null))
const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b)
const mismosIds = (a, b) => [...(a || [])].sort().join(',') === [...(b || [])].sort().join(',')

// The organizations a new contact will be linked to (rows without one are left out).
export const vinculosParaEnviar = (vinculos) => (vinculos || [])
  .filter(v => v.organizacion_id)
  .map(v => ({ organizacion_id: v.organizacion_id, cargo: limpio(v.cargo) }))

// What to send: everything for a new one (inicial = null, links included); only
// what changed when editing (links are edited in the Organizaciones tab).
export function datosContacto(form, inicial) {
  const out = {}
  for (const k of CAMPOS) {
    const v = limpio(form[k])
    if (!inicial || v !== limpio(inicial[k])) out[k] = v
  }
  const correos = filasParaEnviar(form.correos, true)
  const telefonos = filasParaEnviar(form.telefonos)
  if (!inicial || !igual(correos, filasParaEnviar(inicial.correos, true))) out.correos = correos
  if (!inicial || !igual(telefonos, filasParaEnviar(inicial.telefonos))) out.telefonos = telefonos
  if (!inicial || !mismosIds(form.etiqueta_ids, inicial.etiqueta_ids)) out.etiqueta_ids = [...(form.etiqueta_ids || [])]
  if (!inicial) out.vinculos = vinculosParaEnviar(form.vinculos)
  return out
}

// Unsaved changes: against the contact being edited (its fields, or an
// organization chosen for one that had none), or against an empty new form.
export function hayCambios(form, inicial) {
  if (inicial) return Object.keys(datosContacto(form, inicial)).length > 0 || vinculosParaEnviar(form.vinculos).length > 0
  const d = datosContacto(form, null)
  return CAMPOS.some(k => d[k] != null) || d.correos.length > 0 || d.telefonos.length > 0
    || d.etiqueta_ids.length > 0 || (form.vinculos || []).some(v => v.organizacion_id || limpio(v.cargo))
}

// --- Duplicates by name (the server warns by email only) ---

export const nombreNormalizado = (nombre, apellidos) =>
  sinTildes([nombre, apellidos].filter(Boolean).join(' ')).replace(/\s+/g, ' ').trim()

// The contacts (GET /contactos rows) with exactly this name and surnames, accents and case aside.
export function duplicadosPorNombre(lista, nombre, apellidos, excluirId = null) {
  const buscado = nombreNormalizado(nombre, apellidos)
  if (!buscado) return []
  return (lista || []).filter(c => c.id !== excluirId && nombreNormalizado(c.nombre, c.apellidos) === buscado)
}

// As ComercialAvisoDuplicados draws a contact match.
export const coincidenciaDeContacto = (c) => ({
  id: c.id, nombre_completo: c.nombre_completo, correo: c.correo_principal, archivado: !!c.archivado,
  organizaciones: [...new Set(organizacionesDe(c).filter(Boolean))],
})
